import ActiveAdministrationIcon from '@/public/images/nav-menu/active/icon-administration.svg';
import ActiveCISInternalToolsIcon from '@/public/images/nav-menu/active/icon-cis-internal.svg';
import ActiveDataManagementIcon from '@/public/images/nav-menu/active/icon-data-management.svg';
import ActiveEDAIcon from '@/public/images/nav-menu/active/icon-eda.svg'; //different
import ActiveHomeIcon from '@/public/images/nav-menu/active/icon-home.svg';
import ActiveIBankReportingIcon from '@/public/images/nav-menu/active/icon-ibank.svg';
import ActiveSpendManagementIcon from '@/public/images/nav-menu/active/icon-spend-management.svg';
import ActiveTravelManagementIcon from '@/public/images/nav-menu/active/icon-travel-management.svg';
import ActiveTravelSpendIcon from '@/public/images/nav-menu/active/icon-travel-spend-management.svg';
import InactiveAdministrationIcon from '@/public/images/nav-menu/inactive/icon-administration-inactive.svg';
import InactiveCISInternalToolsIcon from '@/public/images/nav-menu/inactive/icon-cis-internal-inactive.svg';
import InactiveDataManagementIcon from '@/public/images/nav-menu/inactive/icon-data-management-inactive.svg';
import InactiveEDAIcon from '@/public/images/nav-menu/inactive/icon-eda-inactive.svg'; //different
import InactiveHomeIcon from '@/public/images/nav-menu/inactive/icon-home-inactive.svg';
import InactiveIBankReportingIcon from '@/public/images/nav-menu/inactive/icon-ibank-inactive.svg';
import InactiveSpendManagementIcon from '@/public/images/nav-menu/inactive/icon-spend-management-inactive.svg';
import InactiveTravelManagementIcon from '@/public/images/nav-menu/inactive/icon-travel-management-inactive.svg';
import InactiveTravelSpendIcon from '@/public/images/nav-menu/inactive/icon-travel-spend-management-inactive.svg';
import { SessionOrg, SessionUser } from '@/types/session.types';
import { effectiveShowHome } from '@/utils/whiteLabeling/config';
import type { SvgIconComponent } from '@mui/icons-material';
import type { ComponentType } from 'react';

export interface NavIcon {
  src?: string;
  activeSrc?: string;
  alt?: string;
  Icon?: SvgIconComponent | ComponentType;
}

export interface NavigationItem {
  label: string;
  icon?: NavIcon;
  url?: string;
  permission?: string;
  isDrawer?: boolean;
  items?: NavigationItem[];
  id?: number;
  disabled?: boolean;
  favorite?: boolean;
  specialConsideration?: (
    item: NavigationItem,
    user: SessionUser,
    org: SessionOrg
  ) => boolean;
}

export const baseNavigationItems: NavigationItem[] = [
  // Homepage.
  {
    label: 'Home',
    icon: {
      src: InactiveHomeIcon,
      activeSrc: ActiveHomeIcon,
      alt: 'Home',
    },
    url: '/hub',
    specialConsideration: (_item, _user, org) => effectiveShowHome(org),
  },

  // Travel Management Drawer.
  {
    label: 'Travel Management',
    icon: {
      src: InactiveTravelManagementIcon,
      activeSrc: ActiveTravelManagementIcon,
      alt: 'Travel Management',
    },
    isDrawer: true,
    items: [
      {
        label: 'QC, Mail, Ticketing',
        permission: 'qcMailTicketing',
        items: [
          {
            label: 'Schedules',
            url: '/travel-management/qc-mail-ticketing/schedules',
          },
          {
            label: 'Tables',
            url: '/travel-management/qc-mail-ticketing/tables',
          },
          {
            label: 'ResRules',
            url: '/travel-management/qc-mail-ticketing/resrules',
          },
          {
            label: 'Operations',
            url: '/travel-management/qc-mail-ticketing/operations',
          },
          {
            label: 'Data Insights',
            url: '/travel-management/qc-mail-ticketing/data-insights',
          },
          {
            label: 'Messaging',
            url: '/travel-management/qc-mail-ticketing/messaging',
          },
        ],
      },
      {
        label: 'Queue Monitor',
        url: '/travel-management/queue-monitor',
      },
      {
        label: 'Table Connector',
        url: '/travel-management/table-connector',
      },
      {
        label: 'TicketTRAK™',
        url: '/travel-management/tickettrak',
      },
      {
        label: 'Travel Authorization',
        permission: 'travelAuthorization',
        items: [
          {
            label: 'Requests',
            url: '/travel-management/travel-authorization/requests',
            permission: 'travelAuthorizationRequests',
          },
          {
            label: 'Travel Policy',
            url: '/travel-management/travel-authorization/travel-policy',
          },
          {
            label: 'Authorization Flow',
            url: '/travel-management/travel-authorization/authorization-flow',
          },
          {
            label: 'Meetings',
            url: '/travel-management/travel-authorization/meetings',
          },
          {
            label: 'Meetings & Events',
            url: '/travel-management/travel-authorization/meetings-plus',
            permission: 'travelAuthorizationDynamicMeetings',
          },
          {
            label: 'Business Insights',
            url: '/travel-management/travel-authorization/business-insights',
          },
          {
            label: 'Transaction Logs',
            url: '/travel-management/travel-authorization/transaction-logs',
          },
          {
            label: 'Document Logs',
            url: '/travel-management/travel-authorization/document-logs',
          },
        ],
      },
      {
        label: 'Reports',
        permission: 'reports',
        items: [
          {
            label: 'Travel',
            url: '/travel-management/reports/travel',
          },
          {
            label: 'Operations',
            url: '/travel-management/reports/operations',
          },
          {
            label: 'iBank',
            url: '/travel-management/reports/ibank',
          },
          {
            label: 'Expedia',
            url: '/travel-management/reports/expedia',
            permission: 'expediaReporting',
          },
        ],
      },
    ],
  },

  // Sigma Reports
  {
    label: 'Spend Management',
    icon: {
      src: InactiveSpendManagementIcon,
      activeSrc: ActiveSpendManagementIcon,
      alt: 'Spend Management',
    },
    url: '/spend-management',
  },
  {
    label: 'Travel + Spend',
    icon: {
      src: InactiveTravelSpendIcon,
      activeSrc: ActiveTravelSpendIcon,
      alt: 'Travel + Spend',
    },
    url: '/travel-spend',
  },
  {
    label: 'Data Management',
    permission: 'dataManagement',
    icon: {
      src: InactiveDataManagementIcon,
      activeSrc: ActiveDataManagementIcon,
      alt: 'Data Management',
    },
    items: [
      {
        label: 'Data Cleanser',
        url: '/data-management/data-cleanser',
        permission: 'dataManagementDataCleanser',
      },
      {
        label: 'Reports',
        url: '/data-management/reports',
        permission: 'dataManagementReports',
      },
    ],
  },

  // EDA
  {
    label: 'Event Driven Actions',
    icon: {
      src: InactiveEDAIcon,
      activeSrc: ActiveEDAIcon,
      alt: 'Event Driven Actions',
    },
    url: '/event-driven-actions',
  },

  // iBank Reporting
  {
    label: 'iBank Reporting',
    icon: {
      src: InactiveIBankReportingIcon,
      activeSrc: ActiveIBankReportingIcon,
      alt: 'iBank Reporting',
    },
    permission: 'ibankReporting',
    items: [
      {
        label: 'Log in to iBank',
        url: '/ibank-reporting/login',
      },
      {
        label: 'Event Logs',
        url: '/ibank-reporting/event-logs',
      },
      {
        label: 'Reports',
        url: '/ibank-reporting/reports',
      },
    ],
  },

  // Administration
  {
    label: 'Administration',
    icon: {
      src: InactiveAdministrationIcon,
      activeSrc: ActiveAdministrationIcon,
      alt: 'Administration',
    },
    permission: 'administration',
    items: [
      {
        label: 'Client Management',
        url: '/administration/client-management',
      },
      {
        label: 'Site Configuration',
        url: '/administration/site-configuration',
      },
      {
        label: 'Asset Management',
        url: '/administration/asset-management',
      },
      {
        label: 'User Management',
        url: '/administration/user-management',
      },
      {
        label: 'Role Management',
        isDrawer: true,
        items: [
          {
            label: 'Module Access',
            url: '/administration/role-management/module-access',
          },
          {
            label: 'Data Access',
            url: '/administration/role-management/data-access',
          },
        ],
      },
      {
        label: 'Activity Logs',
        url: '/administration/activity-log',
      },
      {
        label: 'Broadcasts',
        url: '/administration/broadcasts',
      },
    ],
  },

  // CIS Internal Tools
  {
    label: 'CIS Internal Tools',
    icon: {
      src: InactiveCISInternalToolsIcon,
      activeSrc: ActiveCISInternalToolsIcon,
      alt: 'CIS Internal Tools',
    },
    permission: 'cisInternal',
    items: [
      {
        label: 'Organizations',
        url: '/cis-internal-tools/organizations',
      },
      {
        label: 'Libraries',
        url: '/cis-internal-tools/libraries',
      },
      {
        label: 'Schedule Mover',
        url: '/cis-internal-tools/schedule-mover',
      },
      {
        label: 'Engine Monitor',
        url: '/cis-internal-tools/engine-monitor',
      },
      {
        label: 'Queue Monitor',
        url: '/cis-internal-tools/queue-monitor',
      },
      {
        label: 'iQCX Alert Notifications',
        url: '/cis-internal-tools/iqcx-alert-notifications',
      },
      {
        label: 'Email Notifications',
        url: '/cis-internal-tools/email-notification',
      },
      {
        label: 'Parameter Configuration',
        url: '/cis-internal-tools/parameter-configuration',
      },
      {
        label: 'Dynamic Reporting',
        url: '/cis-internal-tools/dynamic-reporting',
      },
      {
        label: 'GTR Stats',
        url: '/cis-internal-tools/gtr-stats',
      },
      {
        label: 'Feature Flag Management',
        url: '/cis-internal-tools/feature-flag-management',
      },
    ],
  },
];
